// VER-218: dedicated axios client for the VerifyU Console API.
// Separate from the legacy src/utils/axiosConfig.js (which sends x-api-key from
// localStorage). This one uses the in-memory Bearer access token + the httpOnly
// refresh cookie, and talks to /console/* (proxied to the local backend in dev).
import axios from "axios";
import { getAccessToken, setAccessToken, clearAccessToken } from "./tokenStore";
import { getEnv } from "./envStore";

// Where the Console API lives. Dev: unset -> "/console" (Vite proxy forwards it to the
// backend). Prod on Vercel: set VITE_CONSOLE_API_BASE="/capi" so calls hit a Vercel
// rewrite that reverse-proxies /capi/* -> <backend>/console/* (see vercel.json). Using a
// prefix that does NOT collide with the SPA's own /console/* page routes keeps the API
// same-origin, so the refresh cookie stays FIRST-party (no third-party-cookie blocking).
const consoleApi = axios.create({
  baseURL: import.meta.env.VITE_CONSOLE_API_BASE || "/console",
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // send & receive the refresh cookie
});

// Attach the Bearer access token (from memory) + the current environment ("mode") to
// every request. Identity endpoints ignore X-Env; env-scoped ones use it to pick the
// preprod/prod data plane. Default is preprod — prod is an explicit opt-in.
consoleApi.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Env"] = getEnv();
  return config;
});

// --- silent refresh on 401 (activity-driven, single-flight) ---
let refreshPromise = null; // shared across concurrent 401s so we refresh once
let onSessionExpired = null; // set by SessionProvider to hard-logout on failure

export function setOnSessionExpired(cb) {
  onSessionExpired = cb;
}

// Endpoints that must NOT trigger a refresh-retry: the refresh call itself (loop
// guard) and the pre-session endpoints, where a 401 is a real auth failure.
const NO_REFRESH = [
  "/auth/refresh",
  "/auth/login",
  "/auth/otp/verify",
  "/auth/otp/resend",
  "/auth/logout",
];

consoleApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    if (status !== 401 || original._retry || NO_REFRESH.includes(original.url)) {
      return Promise.reject(error);
    }
    original._retry = true; // retry each request at most once

    try {
      // Single-flight: many concurrent 401s share one refresh call.
      if (!refreshPromise) {
        refreshPromise = consoleApi
          .post("/auth/refresh")
          .then((r) => setAccessToken(r.data.data.access))
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      return consoleApi(original); // retry with the fresh token (request interceptor re-adds it)
    } catch (e) {
      clearAccessToken();
      if (onSessionExpired) onSessionExpired();
      return Promise.reject(e);
    }
  }
);

// --- Auth API (matches the Step-4 contract) ---
export const authApi = {
  login: (email, password) => consoleApi.post("/auth/login", { email, password }),
  verifyOtp: (otpToken, code) =>
    consoleApi.post("/auth/otp/verify", { otp_token: otpToken, code }),
  resendOtp: (otpToken) => consoleApi.post("/auth/otp/resend", { otp_token: otpToken }),
  refresh: () => consoleApi.post("/auth/refresh"),
  logout: () => consoleApi.post("/auth/logout"),
  me: () => consoleApi.get("/auth/me"),
  forgotPassword: (email) => consoleApi.post("/auth/forgot-password", { email }),
  validateReset: (token) =>
    consoleApi.get(`/auth/reset-password/${encodeURIComponent(token)}`),
  resetPassword: (token, password) =>
    consoleApi.post(`/auth/reset-password/${encodeURIComponent(token)}`, { password }),
  // Step-up (re-OTP) for sensitive admin actions. init emails a code; verify
  // exchanges the code for a short-lived step_up_token (see useStepUp).
  stepUpInit: () => consoleApi.post("/auth/step-up/init"),
  stepUp: (code) => consoleApi.post("/auth/step-up", { code }),
};

// Sensitive admin endpoints carry the step-up token in this header; harmless when
// absent (the backend only enforces it on step-up-gated routes).
const stepUp = (token) => (token ? { headers: { "X-Step-Up-Token": token } } : {});

// --- User management (Account Admin / Internal Admin) — Step 8 backend ---
export const usersApi = {
  list: (params) => consoleApi.get("/users", { params }),
  get: (id) => consoleApi.get(`/users/${id}`),
  apiCollections: () => consoleApi.get("/users/api-collections"),
  // The APIs the caller may grant to a member — bounded by their account's entitlement.
  // Returns { allApis, apiCollections }. Use this (not apiCollections) for member pickers.
  grantableApis: () => consoleApi.get("/users/grantable-apis"),
  // Internal admin: find an existing (e.g. legacy api-key) user to onboard.
  searchExisting: (q) => consoleApi.get("/users/existing", { params: { q } }),
  changeRole: (id, role, token) =>
    consoleApi.patch(`/users/${id}/role`, { role }, stepUp(token)),
  setApiPerms: (id, action, apiNames, token) =>
    consoleApi.patch(`/users/${id}/api-perms`, { action, apiNames }, stepUp(token)),
  setPreprodOnly: (id, value, token) =>
    consoleApi.patch(`/users/${id}/preprod-only`, { value }, stepUp(token)),
  remove: (id, token) => consoleApi.delete(`/users/${id}`, stepUp(token)),
};

// --- Account (self) + cross-tenant account creation (Internal Admin) ---
export const accountApi = {
  get: () => consoleApi.get("/account"),
  setAllowedDomains: (domains, token) =>
    consoleApi.patch("/account/allowed-domains", { domains }, stepUp(token)),
  // Internal Admin only (backend gates on role).
  listAll: () => consoleApi.get("/accounts"),
  getOne: (id) => consoleApi.get(`/accounts/${id}`),
  create: (payload) => consoleApi.post("/accounts", payload),
  setActive: (id, value, token) =>
    consoleApi.patch(`/accounts/${id}/active`, { value }, stepUp(token)),
  setAllowedDomainsById: (id, domains, token) =>
    consoleApi.patch(`/accounts/${id}/allowed-domains`, { domains }, stepUp(token)),
  setBrand: (id, brandId) => consoleApi.patch(`/accounts/${id}/brand`, { brandId }),
  // VER-222 account entitlement (the cap for member grants). Read is internal-admin; the
  // write is step-up-gated. payload: { allApis?, prodEnabled?, apiCollections? }.
  getEntitlements: (id) => consoleApi.get(`/accounts/${id}/entitlements`),
  setEntitlements: (id, payload, token) =>
    consoleApi.patch(`/accounts/${id}/entitlements`, payload, stepUp(token)),
};

// --- Brand (email sender identity) management — internal admin ---
export const brandsApi = {
  list: () => consoleApi.get("/brands"),
  create: (payload, token) => consoleApi.post("/brands", payload, stepUp(token)),
  update: (id, payload, token) => consoleApi.patch(`/brands/${id}`, payload, stepUp(token)),
};

// --- API-catalog management (internal admin) — add/remove catalog entries ---
// Add/remove are step-up-gated + audit-logged server-side.
export const apiCatalogApi = {
  list: () => consoleApi.get("/api-collection"),
  add: (apis, token) => consoleApi.post("/api-collection", { apis }, stepUp(token)),
  remove: (ids, token) => consoleApi.post("/api-collection/delete", { ids }, stepUp(token)),
};

// --- Admin invite management (list/create/resend/revoke) ---
export const invitesAdminApi = {
  list: (params) => consoleApi.get("/invites", { params }),
  // payload: { email, role, accountId? } — accountId honored only for internal_admin.
  create: (payload) => consoleApi.post("/invites", payload),
  resend: (id) => consoleApi.post(`/invites/${id}/resend`),
  revoke: (id) => consoleApi.delete(`/invites/${id}`),
};

// --- Env-scoped "me" ---
// The interceptor already sends the current X-Env, but these take an explicit `env`
// so an env-scoped screen states which data plane it's asking for (clearer, and makes
// the re-fetch-on-toggle dependency explicit rather than hidden in global state).
export const meApi = {
  apis: (env) => consoleApi.get("/me/apis", env ? { headers: { "X-Env": env } } : undefined),
};

// --- Audit log (read-only) ---
export const auditApi = {
  list: (params) => consoleApi.get("/audit-log", { params }),
};

// --- Invite API (public: the invitee has no session yet) ---
export const inviteApi = {
  validate: (token) => consoleApi.get(`/invites/${encodeURIComponent(token)}`),
  accept: (token, password) =>
    consoleApi.post(`/invites/${encodeURIComponent(token)}/accept`, { password }),
};

export default consoleApi;
